import { api } from "./client";

// "board" = „Таблица": фиксирана мрежа колони × редове клетки (размерът се
// избира на самия екран и се пази на компютъра — boardLayout.ts). Кросрепо
// инвариант: същите ключове в BE `MonitorView` (monitors/schemas.py).
export type MonitorView = "grid" | "list" | "board";
// По какво се оцветяват редовете на екрана на монитора; "status" — шпикер:
// готовите редове в `readyColor`.
export type MonitorColorBy = "table" | "waiter" | "station" | "status" | "none";
// Кухненският статус на ред: "0" Незапочнато, "1" В процес, "2" Сготвено,
// "3" Издадено. Кросрепо инвариант: BE `ItemStatus` (bills/schemas.py) —
// с него са и „вижда след"/„сервира от" на монитора.
export type KitchenStatus = "0" | "1" | "2" | "3";
// Докъде монитор на заведението може да доведе ред на всяка станция
// ({stationId: статус}; липсваща станция = "0" — само принтер, статусът не
// мърда): кухненски монитор → "2", шпикер → "3". Прагът „сервира след" (и
// „вижда след" на сервитьорския) на реда не е след това — иначе ред на
// станция без шпикер не би се сервирал никога. BE monitors/serving.py.
export type ReachableStatusByStation = Partial<Record<string, KitchenStatus>>;
// „Сервира след" по станция ({stationId: статус}): прагът на сервитьорския
// монитор, който показва станцията. Липсваща станция — без ограничение (няма
// сервитьорски монитор за нея). BE monitors/serving.py.
export type ServeFromStatusByStation = Partial<Record<string, KitchenStatus>>;
// Подредбата на кутийките на екрана: отгоре надолу (досегашното) или отляво
// надясно. Кросрепо инвариант: BE `MonitorBoxOrder` (monitors/schemas.py).
export type MonitorBoxOrder = "down" | "across";
// Звукът на екрана при нова поръчка — всеки монитор със свой. Кросрепо
// инвариант 41: същите ключове са в BE `MonitorSound` (monitors/schemas.py);
// файловете, етикетите и списъкът за избор са в src/pages/monitors/monitorSounds.ts.
export type MonitorSound =
  | "classic"
  | "bell"
  | "doublebell"
  | "triangle"
  | "chime"
  | "knock"
  | "buzzer"
  | "beeper"
  | "alarm"
  | "ring";

// Къде отива селекцията на екрана след „Изпълни"/„Върни": на реда, заел мястото
// на изпълнения (досегашното), на първия ред горе вляво, на първия ред от
// същата поръчка, или никъде (до следващ клик/↓). Кросрепо инвариант: същите
// ключове са в BE `MonitorSelectionAfterAction` (monitors/schemas.py).
export type MonitorSelectionAfterAction = "stay" | "first" | "order" | "none";

// Видът монитор — `users.monitor_type` в org DB, избира се в Организация.
// Кросрепо инвариант: същите стойности в BE `MonitorType`
// (external/users/models.py). Правилата на екрана по вид —
// src/pages/monitors/screenRules.ts (сервитьорският сервира реда).
export type MonitorType = "kitchen" | "shpicker" | "waiter";

export interface Monitor {
  id: number;
  userId: number;
  stationIds: number[];
  aggregatedQuantities: boolean;
  completeFullQuantity: boolean;
  view: MonitorView;
  // „Вижда след" — шпикер и сервитьорски (users.monitor_type): редът се вижда,
  // щом стигне този кухненски статус (включително). Кухненският не я чете.
  // Липсва при стар BE → както досега (шпикер "2").
  visibleFromStatus?: KitchenStatus;
  // Само сервитьорски: от кой кухненски статус може да се сервира — за
  // станциите на ТОЗИ монитор (правилото по станция — GET /monitors/serve-rule).
  serveFromStatus?: KitchenStatus;
  // Шпикер с оцветяване „По статус": цветът на готовите редове (#RRGGBB).
  readyColor?: string;
  // Подредбата на кутийките; липсва при стар BE → "down".
  boxOrder?: MonitorBoxOrder;
  colorBy: MonitorColorBy;
  // Разделен екран: лявата/дясната половина слушат свои станции. stationIds
  // е ВИНАГИ обединението им (BE го извежда при запис); при splitScreen=false
  // двата списъка са празни.
  splitScreen: boolean;
  leftStationIds: number[];
  rightStationIds: number[];
  // Ключ от MONITOR_SOUNDS; низ, защото непознат (по-нов BE) или липсващ
  // (стар BE) се чете като звука по подразбиране — resolveMonitorSound.
  sound?: string;
  // Липсва при стар BE → "stay".
  selectionAfterAction?: MonitorSelectionAfterAction;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMonitorRequest {
  userId: number;
  stationIds: number[];
  aggregatedQuantities: boolean;
  completeFullQuantity: boolean;
  view: MonitorView;
  visibleFromStatus: KitchenStatus;
  serveFromStatus: KitchenStatus;
  readyColor: string;
  boxOrder: MonitorBoxOrder;
  colorBy: MonitorColorBy;
  // При splitScreen=true BE изисква и двете половини непразни и сам извежда
  // stationIds като тяхното обединение.
  splitScreen: boolean;
  leftStationIds: number[];
  rightStationIds: number[];
  sound: MonitorSound;
  selectionAfterAction: MonitorSelectionAfterAction;
}

export interface UpdateMonitorRequest {
  userId: number;
  stationIds: number[];
  aggregatedQuantities: boolean;
  completeFullQuantity: boolean;
  view: MonitorView;
  visibleFromStatus: KitchenStatus;
  serveFromStatus: KitchenStatus;
  readyColor: string;
  boxOrder: MonitorBoxOrder;
  colorBy: MonitorColorBy;
  splitScreen: boolean;
  leftStationIds: number[];
  rightStationIds: number[];
  sound: MonitorSound;
  selectionAfterAction: MonitorSelectionAfterAction;
}

export interface MonitorInfoStation {
  id: number;
  name: string;
}

export interface MonitorInfoResponse {
  userId: number;
  stationIds: number[];
  stations: MonitorInfoStation[];
  aggregatedQuantities: boolean;
  completeFullQuantity: boolean;
  view?: MonitorView;
  // „Вижда след" (виж Monitor). Липсва при стар BE или стар отговор в
  // огледалото на касата (локален режим) — тогава важи `showAllStatuses`.
  visibleFromStatus?: KitchenStatus;
  // Сервитьорски: прагът за „Изпълни" на ТОЗИ монитор — записаният (BE
  // monitors/serving.py). Липсва → без ограничение.
  // Огледалото на касата пази последния облачен отговор, но в локален режим
  // екранът не прилага прага (Monitors.tsx) — касата сервира без него.
  serveFromStatus?: KitchenStatus;
  // Сервитьорски: таванът на прага и на „вижда след" по станциите на реда
  // (виж ReachableStatusByStation). Липсва/null (стар BE, грешка в BE) → без
  // таван.
  reachableStatusByStation?: ReachableStatusByStation | null;
  readyColor?: string;
  boxOrder?: MonitorBoxOrder;
  // Остарял: досегашният „Всички статуси" на шпикера — само резерва за
  // отговорите без `visibleFromStatus`; новият BE го праща още един релийз.
  showAllStatuses?: boolean;
  colorBy?: MonitorColorBy;
  // Видът от users.monitor_type в org DB; "shpicker" = агрегиращ екран.
  // Липсва при стар BE или стар отговор в огледалото на касата (локален
  // режим) — тогава важи досегашният булев `isShpicker`.
  monitorType?: MonitorType;
  // Остарял: досегашният флаг users.shpicker — само резерва за отговорите без
  // `monitorType` (виж горе); новият BE го праща още един релийз.
  isShpicker?: boolean;
  // Разделен екран (виж Monitor) — опционални за съвместимост със стар BE.
  splitScreen?: boolean;
  leftStationIds?: number[];
  rightStationIds?: number[];
  // Звукът на ТОЗИ екран. Липсва при стар BE или стар отговор в огледалото
  // на касата (локален режим) → звукът по подразбиране.
  sound?: string;
  // Липсва при стар BE или стар отговор в огледалото на касата → "stay".
  selectionAfterAction?: MonitorSelectionAfterAction;
}

export const createMonitor = async (
  data: CreateMonitorRequest
): Promise<Monitor> => {
  const response = await api.post<Monitor>("/monitors/", data);
  return response.data;
};

export const updateMonitor = async (
  monitorId: number,
  data: UpdateMonitorRequest
): Promise<Monitor> => {
  const response = await api.put<Monitor>(`/monitors/${monitorId}`, data);
  return response.data;
};

export const deleteMonitor = async (monitorId: number): Promise<void> => {
  await api.delete(`/monitors/${monitorId}`);
};

export const getMonitors = async (): Promise<Monitor[]> => {
  const response = await api.get<Monitor[]>("/monitors/");
  return response.data;
};

export const getMonitorInfo = async (
  userId: number
): Promise<MonitorInfoResponse> => {
  const response = await api.get<MonitorInfoResponse>(
    `/monitors/info/${userId}`
  );
  return response.data;
};

// Правилото за сервиране (BE monitors/serving.py): от кой кухненски статус
// ред може да се сервира — по станция (serveFromStatusByStation). Прагът на
// РЕДА е най-разрешаващият сред станциите му с праг, с таван по станциите му
// (reachableStatusByStation); ред без станция с праг — без ограничение. Същата
// проверка прави PATCH /bills/items/served.
export interface ServeRuleResponse {
  // Най-разрешаващият праг сред сервитьорските монитори; null — няма
  // сервитьорски монитор (без ограничение — useServeRule връща null).
  serveFromStatus: KitchenStatus | null;
  // Липсва при стар BE → важи serveFromStatus за всеки ред.
  serveFromStatusByStation?: ServeFromStatusByStation;
  reachableStatusByStation?: ReachableStatusByStation;
}

export const getServeRule = async (): Promise<ServeRuleResponse> => {
  const response = await api.get<ServeRuleResponse>("/monitors/serve-rule");
  return response.data;
};
